import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/base/SEO';

export default function CareersPage() {
  const { t } = useTranslation();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://braincommerce.com';
  const [expandedPosition, setExpandedPosition] = useState<string | null>(null);

  const positions = [
    { id: 'pos1', key: 'pos1' },
    { id: 'pos2', key: 'pos2' },
    { id: 'pos3', key: 'pos3' },
    { id: 'pos4', key: 'pos4' },
    { id: 'pos5', key: 'pos5' },
    { id: 'pos6', key: 'pos6' },
  ];

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Careers at BrainCommerce",
    "description": "Join BrainCommerce and help design the future of work. Explore open positions in AI, robotics, and workforce solutions.",
    "url": `${siteUrl}/careers`
  };

  return (
    <>
      <SEO
        title="Careers at BrainCommerce - Join Us in Designing the Future of Work"
        description="Join BrainCommerce and help design the future of work. Explore open positions in AI, robotics, workforce solutions, and more. Be part of our innovative team."
        keywords="BrainCommerce careers, jobs, AI jobs, robotics jobs, workforce solutions careers, employment opportunities"
        canonical={`${siteUrl}/careers`}
        schema={schema}
      />
      
      <div className="min-h-screen bg-white pt-16 md:pt-20">
        {/* Hero Section */}
        <section className="py-12 md:py-20 px-4 md:px-8 lg:px-32 bg-gradient-to-b from-[#fafafa] to-white">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-extralight text-[#1a1a1a] mb-4 md:mb-6 tracking-tight">
              {t('careers.hero.title')}
            </h1>
            <p className="text-base md:text-lg text-[#4a4a4a] mb-6 md:mb-8 leading-relaxed">
              {t('careers.hero.subtitle')}
            </p>
            <a 
              href="#positions"
              className="inline-flex items-center gap-2 px-6 md:px-8 py-2 md:py-3 bg-[#1a1a1a] text-white rounded-full hover:bg-[#2a2a2a] transition-all whitespace-nowrap cursor-pointer text-sm md:text-base"
            >
              {t('careers.hero.cta')}
              <i className="ri-arrow-down-line"></i>
            </a>
          </div>
        </section>

        {/* Culture Values */}
        <section className="py-12 md:py-20 px-4 md:px-8 lg:px-32">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl md:text-4xl font-light text-[#1a1a1a] mb-8 md:mb-12 text-center tracking-tight">
              {t('careers.culture.title')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              <div className="bg-white rounded-lg p-4 md:p-6 shadow-[0_4px_24px_rgba(0,0,0,0.06)]">
                <i className="ri-time-line text-2xl md:text-3xl text-[#1a1a1a] mb-3 md:mb-4"></i>
                <h3 className="text-base md:text-lg font-semibold text-[#1a1a1a] mb-2">{t('careers.culture.value1.title')}</h3>
                <p className="text-sm md:text-base text-[#4a4a4a]">{t('careers.culture.value1.description')}</p>
              </div>
              <div className="bg-white rounded-lg p-4 md:p-6 shadow-[0_4px_24px_rgba(0,0,0,0.06)]">
                <i className="ri-shield-check-line text-2xl md:text-3xl text-[#1a1a1a] mb-3 md:mb-4"></i>
                <h3 className="text-base md:text-lg font-semibold text-[#1a1a1a] mb-2">{t('careers.culture.value2.title')}</h3>
                <p className="text-sm md:text-base text-[#4a4a4a]">{t('careers.culture.value2.description')}</p>
              </div>
              <div className="bg-white rounded-lg p-4 md:p-6 shadow-[0_4px_24px_rgba(0,0,0,0.06)]">
                <i className="ri-pencil-ruler-2-line text-2xl md:text-3xl text-[#1a1a1a] mb-3 md:mb-4"></i>
                <h3 className="text-base md:text-lg font-semibold text-[#1a1a1a] mb-2">{t('careers.culture.value3.title')}</h3>
                <p className="text-sm md:text-base text-[#4a4a4a]">{t('careers.culture.value3.description')}</p>
              </div>
              <div className="bg-white rounded-lg p-4 md:p-6 shadow-[0_4px_24px_rgba(0,0,0,0.06)]">
                <i className="ri-team-line text-2xl md:text-3xl text-[#1a1a1a] mb-3 md:mb-4"></i>
                <h3 className="text-base md:text-lg font-semibold text-[#1a1a1a] mb-2">{t('careers.culture.value4.title')}</h3>
                <p className="text-sm md:text-base text-[#4a4a4a]">{t('careers.culture.value4.description')}</p>
              </div>
              <div className="bg-white rounded-lg p-4 md:p-6 shadow-[0_4px_24px_rgba(0,0,0,0.06)]">
                <i className="ri-lightbulb-line text-2xl md:text-3xl text-[#1a1a1a] mb-3 md:mb-4"></i>
                <h3 className="text-base md:text-lg font-semibold text-[#1a1a1a] mb-2">{t('careers.culture.value5.title')}</h3>
                <p className="text-sm md:text-base text-[#4a4a4a]">{t('careers.culture.value5.description')}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Open Positions */}
        <section id="positions" className="py-12 md:py-20 px-4 md:px-8 lg:px-32 bg-[#fafafa]">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl md:text-4xl font-light text-[#1a1a1a] mb-8 md:mb-12 text-center tracking-tight">
              {t('careers.openings.title')}
            </h2>
            <div className="space-y-3 md:space-y-4">
              {positions.map((position) => (
                <div key={position.id} className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.06)] overflow-hidden">
                  <button
                    onClick={() => setExpandedPosition(expandedPosition === position.id ? null : position.id)}
                    className="w-full px-4 md:px-8 py-4 md:py-6 flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <div className="flex-grow text-left">
                      <h3 className="text-base md:text-xl font-semibold text-[#1a1a1a] mb-1 md:mb-2">
                        {t(`careers.positions.${position.key}.title`)}
                      </h3>
                      <div className="flex flex-wrap gap-2 md:gap-4 text-xs md:text-sm text-[#4a4a4a]">
                        <span className="flex items-center gap-1">
                          <i className="ri-building-line"></i>
                          {t(`careers.positions.${position.key}.department`)}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-map-pin-line"></i>
                          {t(`careers.positions.${position.key}.location`)}
                        </span>
                      </div>
                    </div>
                    <i className={`ri-arrow-${expandedPosition === position.id ? 'up' : 'down'}-s-line text-xl md:text-2xl text-[#1a1a1a] flex-shrink-0 ml-2`}></i>
                  </button>
                  
                  {expandedPosition === position.id && (
                    <div className="px-4 md:px-8 pb-4 md:pb-6 border-t border-gray-100">
                      <div className="pt-4 md:pt-6">
                        <h4 className="text-sm md:text-base font-semibold text-[#1a1a1a] mb-2 md:mb-3">{t('careers.openings.description')}</h4>
                        <p className="text-sm md:text-base text-[#4a4a4a] mb-4 md:mb-6 leading-relaxed">
                          {t(`careers.positions.${position.key}.description`)}
                        </p>
                        
                        <h4 className="text-sm md:text-base font-semibold text-[#1a1a1a] mb-2 md:mb-3">{t('careers.openings.requirements')}</h4>
                        <p className="text-sm md:text-base text-[#4a4a4a] mb-4 md:mb-6 leading-relaxed">
                          {t(`careers.positions.${position.key}.requirements`)}
                        </p>
                        
                        <Link 
                          to="/contact"
                          className="inline-flex items-center gap-2 px-4 md:px-6 py-2 bg-[#1a1a1a] text-white rounded-full hover:bg-[#2a2a2a] transition-all whitespace-nowrap cursor-pointer text-sm md:text-base"
                        >
                          {t('careers.openings.apply')}
                          <i className="ri-arrow-right-line"></i>
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
